import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import HomeFooter from '@/components/ui/HomeFooter';
import useTourGuide from '@/hooks/useTourGuide';
import { gql, useQuery } from '@apollo/client';
import { AlertCircle, Bell, ClipboardList, HelpCircle, Info, TrendingUp, Users } from "lucide-react";
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useUserNotifications } from './hooks/useUserNotifications';
import { useRecentApplications } from './hooks/useUserRecentApplications';

const GET_USER_DETAILS = gql`
   query GetUserDetails {
      me {
         firstName
         lastName
      }
   }
`;

const LoadingSkeleton = () => (
   <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 flex flex-col pt-16">
      <main className="flex-grow mx-auto w-full max-w-[90rem] px-4 sm:px-6 lg:px-8 py-8">
         <div className="max-w-[85rem] mx-auto">
            {/* Welcome Section Skeleton */}
            <div className="text-center mb-8 sm:mb-12">
               <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto mb-3 animate-pulse"></div>
               <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg mx-auto animate-pulse"></div>
            </div>

            {/* Quick Actions Skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-10">
               {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 animate-pulse">
                     <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                     </div>
                     <div className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
               ))}
            </div>

            {/* Main Content Grid Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
               {/* Recent Applications Skeleton */}
               <div className="lg:col-span-7 bg-white dark:bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-6">
                     <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                     <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                  {[...Array(5)].map((_, i) => (
                     <div key={i} className="flex items-center justify-between mb-4 animate-pulse">
                        <div className="flex-1 space-y-2">
                           <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                           <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        </div>
                        <div className="w-20 h-6 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                     </div>
                  ))}
               </div>

               {/* Notifications Skeleton */}
               <div className="lg:col-span-5 bg-white dark:bg-gray-800 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-6">
                     <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                     <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                  {[...Array(4)].map((_, i) => (
                     <div key={i} className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg animate-pulse">
                        <div className="flex space-x-3">
                           <div className="w-6 h-6 bg-gray-200 dark:bg-gray-600 rounded-full"></div>
                           <div className="flex-1 space-y-2">
                              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-600 rounded-lg"></div>
                              <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-600 rounded-lg"></div>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      </main>
   </div>
);

const HomePage = ({ sidebarOpen }) => {
   const { recentApplications, loading: applicationsLoading, error: applicationsError } = useRecentApplications();
   const {
      notifications,
      unreadNotifications,
      loading: notificationsLoading,
      error: notificationsError,
      markAsRead,
      markAllAsRead
   } = useUserNotifications();
   const [recentNotifications, setRecentNotifications] = useState([]);
   const [unreadCount, setUnreadCount] = useState(0);

   const location = useLocation();
   const queryParams = new URLSearchParams(location.search);
   const isNewUser = queryParams.get('newUser') === 'true';

   const { loading: userLoading, error: userError, data: userData } = useQuery(GET_USER_DETAILS);

   const quickActions = useMemo(() => [
      { title: "New Application", icon: <ClipboardList className="h-6 w-6" />, link: "/permits" },
      { title: "My Applications", icon: <ClipboardList className="h-6 w-6" />, link: "/applicationsStatus" },
      { title: "Notifications", icon: <Bell className="h-6 w-6" />, link: "/notifications" },
      { title: "Profile", icon: <Users className="h-6 w-6" />, link: "/profile" },
   ], []);

   const getStatusColor = useCallback((status) => {
      switch (status.toLowerCase()) {
         case 'draft':
            return 'bg-gray-200 text-gray-800';
         case 'submitted':
            return 'bg-blue-200 text-blue-800';
         case 'in progress':
            return 'bg-yellow-200 text-yellow-800';
         case 'approved':
            return 'bg-green-200 text-green-800';
         case 'rejected':
            return 'bg-red-200 text-red-800';
         case 'returned':
            return 'bg-orange-200 text-orange-800';
         case 'released':
            return 'bg-green-200 text-green-800';
         case 'expired':
            return 'bg-red-200 text-red-800';
         case 'renewed':
            return 'bg-green-200 text-green-800';
         default:
            return 'bg-gray-200 text-gray-800';
      }
   }, []);

   const formatNotificationType = (type) => {
      switch (type) {
         case 'APPLICATION_STATUS':
            return 'Application Update';
         case 'SYSTEM':
            return 'System Notice';
         case 'PAYMENT':
            return 'Payment Update';
         default:
            return 'Notification';
      }
   };

   const formatDate = (timestamp) => {
      // const date = new Date(timestamp);
      // return date.toLocaleDateString();
      return new Date(timestamp).toLocaleDateString();
   };

   const { startTour, commonTourSteps } = useTourGuide({
      tourId: 'home-tour',
   });

   const homeTourSteps = [
      {
         target: '.welcome-section',
         content: 'Welcome to your dashboard! This is your central hub for managing all your permit applications.',
         placement: 'bottom',
      },
      {
         target: '.quick-actions',
         content: 'These are your quick actions for common tasks.',
         placement: 'bottom',
      },
      {
         target: '.new-application-btn',
         content: 'Click here to start a new permit application',
         placement: 'bottom',
      },
      {
         target: '.recent-applications',
         content: 'View and manage your most recent applications here',
         placement: 'left',
      },
      {
         target: '.notifications-section',
         content: 'Stay updated with notifications about your applications and important updates',
         placement: 'left',
      }
   ];

   if (userLoading || applicationsLoading) return <LoadingSkeleton />;
   if (userError || applicationsError) return <p>Error: {userError?.message || applicationsError?.message}</p>;

   const { firstName, lastName } = userData?.me || {};

   return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white dark:from-gray-900 dark:to-gray-800 flex flex-col pt-16 transition-colors duration-300">
         <ToastContainer />
         <main className={`flex-grow transition-all duration-300 mx-auto w-full max-w-[90rem] px-4 sm:px-6 lg:px-8 py-8
            ${sidebarOpen ? 'lg:ml-64' : ''}`}
         >
            <div className="max-w-[85rem] mx-auto">
               {/* Welcome Section */}
               <div className="text-center mb-8 sm:mb-12 welcome-section">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">
                     {isNewUser ? "Welcome" : "Welcome back"}, {firstName}!
                  </h1>
                  <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300">
                     Manage your environmental permits and applications all in one place.
                  </p>
                  <Button
                     variant="outline"
                     size="sm"
                     onClick={() => startTour(homeTourSteps, 'home-tour')}
                     className="mt-4"
                  >
                     <HelpCircle className="h-4 w-4 mr-2" />
                     Take a Tour
                  </Button>
               </div>

               {/* Quick Actions */}
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-10 quick-actions">
                  {quickActions.map((action, index) => (
                     <Card key={index} className={`group hover:shadow-xl transition-all duration-300 ${action.title === "New Application" ? "new-application-btn" : ""
                        }`}>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                           <div className="flex items-center space-x-3">
                              <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500 group-hover:bg-green-100 dark:group-hover:bg-green-900/30 transition-colors duration-300">
                                 {action.icon}
                              </div>
                              <CardTitle className="text-base font-semibold text-gray-900 dark:text-white">
                                 {action.title}
                              </CardTitle>
                           </div>
                        </CardHeader>
                        <CardContent>
                           <Button
                              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                              onClick={() => window.location.href = action.link}
                           >
                              Go to {action.title}
                           </Button>
                        </CardContent>
                     </Card>
                  ))}
               </div>

               {/* Main Content Grid */}
               <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
                  {/* Recent Applications Card */}
                  <Card className="lg:col-span-7 hover:shadow-xl transition-all duration-300 flex flex-col recent-applications">
                     <CardHeader className="border-b border-gray-100 pb-3 sm:pb-4">
                        <div className="flex items-center space-x-3">
                           <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500">
                              <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" />
                           </div>
                           <CardTitle>Recent Applications</CardTitle>
                        </div>
                     </CardHeader>
                     <CardContent className="flex-1 pt-4 sm:pt-6">
                        <div className="h-[300px] sm:h-[365px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                           {applicationsLoading ? (
                              <div className="flex items-center justify-center h-[365px]">
                                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                              </div>
                           ) : applicationsError ? (
                              <div className="flex items-center justify-center h-[365px] text-red-500">
                                 <AlertCircle className="h-6 w-6 mr-2" />
                                 <p>Error loading applications</p>
                              </div>
                           ) : recentApplications.length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-[365px] text-gray-500 dark:text-gray-400">
                                 <ClipboardList className="h-12 w-12 mb-3 text-gray-400" />
                                 <p className="text-lg text-gray-500 dark:text-gray-400">No recent applications</p>
                                 <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Start by creating a new application</p>
                              </div>
                           ) : (
                              <div className="space-y-4">
                                 {recentApplications.slice(0, 7).map((app, index) => (
                                    <div
                                       key={index}
                                       className="group flex items-center border-b border-gray-100 dark:border-gray-800 pb-4 last:border-b-0
                                          hover:bg-accent hover:bg-opacity-50 dark:hover:bg-accent/20 rounded-lg transition-all duration-200 -mx-2 px-4"
                                    >
                                       <div className="flex-grow">
                                          <p className="font-semibold text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-500
                                             transition-colors duration-200">
                                             {app.applicationType}
                                          </p>
                                          <div className="flex items-center gap-3 mt-1">
                                             <p className="text-sm text-gray-500 dark:text-gray-400">ID: {app.applicationNumber}</p>
                                             <span className="text-gray-300 dark:text-gray-600">•</span>
                                             <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(app.dateOfSubmission)}
                                             </p>
                                          </div>
                                       </div>
                                       <div className="flex-shrink-0">
                                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium
                                             ${getStatusColor(app.status)}`}>
                                             {app.status}
                                          </span>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           )}
                        </div>
                     </CardContent>
                     <CardFooter className="border-t border-gray-100 pt-4">
                        <Button
                           className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           onClick={() => window.location.href = '/applicationsStatus'}
                        >
                           View All Applications
                        </Button>
                     </CardFooter>
                  </Card>

                  {/* Notifications Card */}
                  <Card className="lg:col-span-5 hover:shadow-xl transition-all duration-300 flex flex-col notifications-section">
                     <CardHeader className="border-b border-gray-100 pb-3 sm:pb-4">
                        <div className="flex items-center space-x-3">
                           <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-500 relative">
                              <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
                              {unreadNotifications.length > 0 && (
                                 <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                                    {unreadNotifications.length}
                                 </span>
                              )}
                           </div>
                           <CardTitle>Notifications</CardTitle>
                        </div>
                     </CardHeader>
                     <CardContent className="flex-1 pt-4 sm:pt-6">
                        <div className="h-[300px] sm:h-[365px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                           {notificationsLoading ? (
                              <div className="flex items-center justify-center h-[365px]">
                                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                              </div>
                           ) : notificationsError ? (
                              <div className="flex items-center justify-center h-[365px] text-red-500">
                                 <AlertCircle className="h-6 w-6 mr-2" />
                                 <p>Error loading notifications</p>
                              </div>
                           ) : notifications.length === 0 ? (
                              <div className="flex flex-col items-center justify-center h-[365px] text-gray-500 dark:text-gray-400">
                                 <Bell className="h-12 w-12 mb-3 text-gray-400" />
                                 <p className="text-lg text-gray-500 dark:text-gray-400">No notifications yet</p>
                                 <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">We'll notify you of important updates</p>
                              </div>
                           ) : (
                              <div className="space-y-3">
                                 {notifications.slice(0, 10).map((notification) => (
                                    <div
                                       key={notification.id}
                                       onClick={() => markAsRead(notification.id)}
                                       className={`group relative rounded-lg p-4 transition-all duration-200
                                          cursor-pointer transform hover:translate-x-1
                                          ${!notification.read
                                             ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                                             : 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800/70'
                                          }`}
                                    >
                                       <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0">
                                             {notification.type === 'APPLICATION_STATUS' ? (
                                                <ClipboardList className="h-5 w-5 text-blue-500" />
                                             ) : notification.type === 'PAYMENT' ? (
                                                <TrendingUp className="h-5 w-5 text-green-500" />
                                             ) : (
                                                <Info className="h-5 w-5 text-green-500" />
                                             )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                             <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-green-700
                                                transition-colors duration-200">
                                                {notification.title}
                                             </p>
                                             <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                                                {notification.message}
                                             </p>
                                             <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                {formatDate(notification.createdAt)}
                                             </p>
                                          </div>
                                          {!notification.read && (
                                             <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-green-500"></div>
                                          )}
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           )}
                        </div>
                     </CardContent>
                     <CardFooter className="border-t border-gray-100 pt-4">
                        <Button
                           className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           onClick={() => window.location.href = '/notifications'}
                        >
                           View All Notifications
                        </Button>
                     </CardFooter>
                  </Card>
               </div>
            </div>
         </main>
         <HomeFooter />
      </div>
   );
};

export default HomePage;
